import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiInfo, FiX, FiXCircle } from 'react-icons/fi';
import './Feedback.css';

const FeedbackContext = createContext(null);

let alertHandler = null;
let confirmHandler = null;
const TOAST_DURATION_MS = 2600;

const ALERT_TITLES = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Notice',
};

/** Drop-in replacement for alert() — pass type explicitly when not info */
export function notify(message, type = 'info') {
  const text = message == null ? '' : String(message);
  if (alertHandler) {
    alertHandler({ type, message: text });
  }
}

function showToast(type, message) {
  const text = String(message ?? '');
  if (alertHandler) {
    alertHandler({ type, message: text });
  } else {
    notify(text, type);
  }
}

export const toast = {
  success(message) {
    showToast('success', message);
  },
  error(message) {
    showToast('error', message);
  },
  info(message) {
    showToast('info', message);
  },
  warning(message) {
    showToast('warning', message);
  },
};

/**
 * Drop-in async replacement for window.confirm().
 * Usage: if (!(await confirm('Are you sure?'))) return;
 */
export function confirm(messageOrOptions, maybeOptions) {
  let options;
  if (typeof messageOrOptions === 'string') {
    options = { message: messageOrOptions, ...(maybeOptions || {}) };
  } else {
    options = { ...(messageOrOptions || {}) };
  }
  if (confirmHandler) return confirmHandler(options);
  if (typeof window !== 'undefined') {
    return Promise.resolve(window.confirm(options.message || ''));
  }
  return Promise.resolve(false);
}

const ALERT_ICONS = {
  success: FiCheckCircle,
  error: FiXCircle,
  warning: FiAlertTriangle,
  info: FiInfo,
};

function ToastViewport({ toasts, onDismiss }) {
  return (
    <div className="fb-toast-viewport" aria-live="polite">
      {toasts.map((item) => {
        const Icon = ALERT_ICONS[item.type] || FiInfo;
        return (
          <div key={item.id} className={`fb-toast fb-toast-${item.type}`} role="status">
            <Icon className="fb-toast-icon" />
            <span className="fb-toast-message">{item.message}</span>
            <button type="button" className="fb-toast-close" onClick={() => onDismiss(item.id)} aria-label="Close">
              <FiX />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function AlertDialog({ state, onDismiss }) {
  if (!state) return null;
  const Icon = ALERT_ICONS[state.type] || FiInfo;
  const title = state.title || ALERT_TITLES[state.type] || 'Notice';
  const okLabel = state.okText || 'OK';

  return (
    <div className="fb-alert-overlay" role="alertdialog" aria-modal="true" aria-labelledby="fb-alert-title">
      <div className="fb-alert-dialog" onClick={(e) => e.stopPropagation()}>
        <div className={`fb-alert-header fb-alert-header-${state.type}`}>
          <Icon className="fb-alert-icon" />
          <h2 id="fb-alert-title" className="fb-alert-title">{title}</h2>
        </div>
        <div className="fb-alert-body">
          <p className="fb-alert-message">{state.message}</p>
        </div>
        <div className="fb-alert-footer">
          <button type="button" className={`fb-alert-ok fb-alert-ok-${state.type}`} onClick={onDismiss} autoFocus>
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({ state, onResolve }) {
  if (!state) return null;
  const title = state.title || 'Confirm';
  const confirmLabel = state.confirmText || 'Confirm';
  const cancelLabel = state.cancelText || 'Cancel';
  const danger = !!state.danger;

  return (
    <div className="fb-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="fb-confirm-title">
      <div className="fb-confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="fb-confirm-header">
          <h2 id="fb-confirm-title" className="fb-confirm-title">{title}</h2>
        </div>
        <div className="fb-confirm-body">
          <p className="fb-confirm-message">{state.message}</p>
        </div>
        <div className="fb-confirm-footer">
          <button type="button" className="fb-confirm-cancel" onClick={() => onResolve(false)}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`fb-confirm-ok${danger ? ' fb-confirm-ok-danger' : ''}`}
            onClick={() => onResolve(true)}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [alertState, setAlertState] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const confirmResolverRef = useRef(null);
  const toastIdRef = useRef(0);

  const dismissAlert = useCallback(() => {
    setAlertState(null);
  }, []);

  const showAlert = useCallback(({ type = 'info', message }) => {
    if (type === 'error' || type === 'warning') {
      setAlertState({ type, message });
      return;
    }

    const id = toastIdRef.current + 1;
    toastIdRef.current = id;
    setToasts((prev) => [...prev, { id, type, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((item) => item.id !== id));
    }, TOAST_DURATION_MS);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const openConfirm = useCallback((options) => {
    return new Promise((resolve) => {
      if (confirmResolverRef.current) {
        confirmResolverRef.current(false);
      }
      confirmResolverRef.current = resolve;
      setConfirmState({
        title: options.title,
        message: options.message || '',
        confirmText: options.confirmText,
        cancelText: options.cancelText,
        danger: options.danger,
      });
    });
  }, []);

  const resolveConfirm = useCallback((value) => {
    const resolver = confirmResolverRef.current;
    confirmResolverRef.current = null;
    setConfirmState(null);
    if (resolver) resolver(value);
  }, []);

  useEffect(() => {
    alertHandler = showAlert;
    confirmHandler = openConfirm;
    return () => {
      if (alertHandler === showAlert) alertHandler = null;
      if (confirmHandler === openConfirm) confirmHandler = null;
    };
  }, [showAlert, openConfirm]);

  const value = useMemo(
    () => ({
      toast,
      notify,
      confirm,
      showAlert,
    }),
    [showAlert]
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
      <AlertDialog state={alertState} onDismiss={dismissAlert} />
      <ConfirmDialog state={confirmState} onResolve={resolveConfirm} />
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    return { toast, notify, confirm };
  }
  return ctx;
}
